import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GoogleMapReact from "google-map-react";
import { defaultStyle, terracottaStyle } from "./MapStyling";
import { Searchbar } from "./Searchbar";
import DestinationsPanel from "./DestinationsPanel";
import { store } from "../../util/store";
import { Button, CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { isMobile } from "../../util/screenSizeHelpers";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { Box } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_DEV_OPTIONS,
  buildMapOptions,
  clampNumber,
  getSafeDevOptions,
  normalizeHexColor,
  shadeHexColor,
} from "./mapDevOptions";
import { getTypeColor, PIN_COLOR } from "./typeColors";

//TODO: implement https://mui.com/material-ui/react-speed-dial/ for submit a destination, submission criteria

const useStyles = makeStyles({
  container: {
    minHeight: 0,
    flex: "1",
    display: "flex",
  },
  mobileContainer: {
    minHeight: 0,
    width: "100%",
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column",
  },
  showMapButton: {
    margin: "10px",
    minHeight: "fit-content",
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  mapContainer: {
    height: (props) => (isMobile(props.mobile) ? "100%" : undefined),
    overflow: "hidden",
    flex: 1,
    position: "relative",
    zIndex: 1,
    marginBottom: 1,
  },
  locationList: {
    display: (props) => (props.showList ? "flex" : "none"),
    flexDirection: "column",
    height: "100%",
    width: (props) => (isMobile(props.mobile) ? "100%" : "22%"),
    maxWidth: (props) => (isMobile(props.mobile) ? "100%" : "50%"),
    overflow: "auto",
    minWidth: (props) => (isMobile(props.mobile) ? 0 : "400px"),
  },
  chip: {
    margin: "15px 20px",
    marginLeft: "0px",
  },
});

export const handleLinkClick = (destination) => {
  let url = destination.url;
  if (url.slice(0, 4) !== "http") {
    url = "http://" + url;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

const defaultCenter = { lat: 59.95, lng: 30.33 };

// Inline SVG pin icons — fallback pins
const PIN_SVG_STARRED = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="26" viewBox="0 0 18 26"><path d="M9 1C4.58 1 1 4.58 1 9c0 6.75 8 16 8 16s8-9.25 8-16c0-4.42-3.58-8-8-8z" fill="${PIN_COLOR}" stroke="#ffffff" stroke-width="2"/><circle cx="9" cy="9" r="3.5" fill="${PIN_COLOR}" stroke="#ffffff" stroke-width="1.25"/><polygon points="9,6.5 9.6,8.3 11.5,8.3 10,9.4 10.5,11.2 9,10.1 7.5,11.2 8,9.4 6.5,8.3 8.4,8.3" fill="#fff" fill-opacity="0.9"/></svg>`;

// Map destination type (lowercase) to custom SVG pin file in public/images/new_markers/
const TYPE_MARKER_MAP = {
  artist: "/images/new_markers/pin-artist.svg",
  store: "/images/new_markers/pin-store.svg",
  shop: "/images/new_markers/pin-store.svg",
  manufacturer: "/images/new_markers/pin-manufacturer.svg",
  museum: "/images/new_markers/pin-museum.svg",
  studio: "/images/new_markers/pin-studio.svg",
  iac: "/images/new_markers/pin-iac.svg",
  institution: "/images/new_markers/pin-institution.svg",
  gallery: "/images/new_markers/pin-gallery.svg",
  residency: "/images/new_markers/pin-residency.svg",
  organization: "/images/new_markers/pin-organization.svg",
  organisation: "/images/new_markers/pin-organization.svg",
  "cultural heritage site":
    "/images/new_markers/pin-cultural-heritage-site.svg",
  chs: "/images/new_markers/pin-cultural-heritage-site.svg",
};

const markerSizeForZoom = (zoom) => {
  const safeZoom = clampNumber(zoom ?? 0, 0, 18);
  const zoomScale = 1 + Math.max(0, safeZoom - 7) * 0.13;
  const scale = Math.min(2, zoomScale);

  return {
    w: Math.round(20 * scale),
    h: Math.round(30 * scale),
  };
};

const markerIconCache = new Map();
const clusterIconCache = new Map();

const getMarkerIconSource = (types, sponsored) => {
  if (types?.length) {
    for (const type of types) {
      const url = TYPE_MARKER_MAP[String(type).toLowerCase()];
      if (url) {
        return { key: `type:${url}`, url, isFallback: false };
      }
    }
  }
  return {
    key: "fallback:iac",
    url: sponsored
      ? `data:image/svg+xml;base64,${window.btoa(PIN_SVG_STARRED)}`
      : "/images/new_markers/pin-iac.svg",
    isFallback: true,
  };
};

const getMarkerIcon = (types, sponsored, zoom, markerSizePercent = 100) => {
  const source = getMarkerIconSource(types, sponsored);
  const sizePercent = clampNumber(markerSizePercent, 50, 200);
  const scale = sizePercent / 100;
  const baseSize = markerSizeForZoom(zoom);
  const w = Math.round(baseSize.w * scale);
  const h = Math.round(baseSize.h * scale);
  const cacheKey = `${w}x${h}:${source.key}`;
  const cachedIcon = markerIconCache.get(cacheKey);
  if (cachedIcon) return cachedIcon;

  if (!source.isFallback) {
    const icon = {
      url: source.url,
      scaledSize: new window.google.maps.Size(w, h),
      anchor: new window.google.maps.Point(w / 2, h),
    };
    markerIconCache.set(cacheKey, icon);
    return icon;
  }

  const icon = {
    url: source.url,
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(w / 2, h),
  };
  markerIconCache.set(cacheKey, icon);
  return icon;
};

const getClusterIcon = (size, color, transparency) => {
  const clusterColor = normalizeHexColor(
    color,
    DEFAULT_DEV_OPTIONS.clusterColor,
  );
  const opacity = 1 - clampNumber(transparency, 0, 100) / 100;
  const strokeColor = shadeHexColor(clusterColor, -30);
  const cacheKey = `${size}:${clusterColor}:${opacity}`;
  const cachedIcon = clusterIconCache.get(cacheKey);
  if (cachedIcon) return cachedIcon;

  const c = size / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" opacity="${opacity}"><circle cx="${c}" cy="${c}" r="${c - 1}" fill="${clusterColor}" stroke="${strokeColor}" stroke-width="2"/></svg>`;
  const icon = {
    url: `data:image/svg+xml;base64,${window.btoa(svg)}`,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(c, c),
  };
  clusterIconCache.set(cacheKey, icon);
  return icon;
};

const createClusterRenderer = (devOptionsRef) => ({
  render({ count, position }) {
    const { clusterColor, clusterSize, clusterTransparency } =
      devOptionsRef.current ?? DEFAULT_DEV_OPTIONS;
    const baseSize = count >= 100 ? 46 : count >= 10 ? 40 : 34;
    const size = Math.round(baseSize * (clusterSize / 100));

    return new window.google.maps.Marker({
      position,
      title: `Cluster of ${count} destinations`,
      icon: getClusterIcon(size, clusterColor, clusterTransparency),
      label: {
        text: String(count),
        color: "#fff",
        fontSize: count >= 100 ? "13px" : "12px",
        fontWeight: "700",
        fontFamily: "Work Sans, Arial, sans-serif",
      },
      optimized: true,
      zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
    });
  },
});

const stripDiacritics = (str) =>
  String(str ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const prepareSearchText = (value) => {
  const text = stripDiacritics(value).toLowerCase();
  return {
    text,
    words: text.split(/[^a-z0-9]+/).filter(Boolean),
  };
};

const createMarkerSearchIndex = (marker) => ({
  name: prepareSearchText(marker.name),
  city: prepareSearchText(marker.city),
  province: prepareSearchText(marker.province),
  country: prepareSearchText(marker.country),
  url: prepareSearchText(marker.url),
});

const editDistance = (a, b) => {
  const m = a.length;
  const n = b.length;
  const row = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = row[j];
      row[j] =
        a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, row[j], row[j - 1]);
      prev = temp;
    }
  }
  return row[n];
};

/**
 * Score how well a single field matches the query.
 * 3 = exact substring (after diacritic normalization)
 * 2 = every query word exactly matches a field word
 * 1 = every query word fuzzy-matches a field word
 * 0 = no match
 */
const fieldMatchScore = (fieldValue, searchQuery) => {
  const fieldData =
    fieldValue && typeof fieldValue === "object"
      ? fieldValue
      : prepareSearchText(fieldValue);
  const queryData =
    searchQuery && typeof searchQuery === "object"
      ? searchQuery
      : prepareSearchText(searchQuery);
  const { text: field, words: fieldWords } = fieldData;
  const { text: query, words: queryWords } = queryData;

  if (!field || !query) return 0;

  if (field.includes(query)) return 3;

  let allExact = true;
  let allFuzzy = true;

  for (const qWord of queryWords) {
    if (qWord.length <= 2) {
      if (!field.includes(qWord)) {
        allExact = false;
        allFuzzy = false;
        break;
      }
      continue;
    }
    const lengthThreshold = qWord.length <= 4 ? 1 : qWord.length <= 7 ? 2 : 3;
    let exactFound = false;
    let fuzzyFound = false;
    for (const fWord of fieldWords) {
      if (fWord === qWord) {
        exactFound = true;
        fuzzyFound = true;
        break;
      }
      const ratioThreshold = Math.floor(
        Math.min(fWord.length, qWord.length) * 0.4,
      );
      const threshold = Math.min(lengthThreshold, ratioThreshold);
      if (editDistance(fWord, qWord) <= threshold) fuzzyFound = true;
    }
    if (!exactFound) allExact = false;
    if (!fuzzyFound) {
      allFuzzy = false;
      break;
    }
  }

  if (allExact) return 2;
  if (allFuzzy) return 1;
  return 0;
};

/**
 * Overall match score for a marker, weighted by field importance.
 * Name matches outrank city/country matches.
 */
const markerMatchScore = (searchIndex, searchQuery) => {
  return Math.max(
    fieldMatchScore(searchIndex.name, searchQuery) * 3,
    fieldMatchScore(searchIndex.city, searchQuery) * 2,
    fieldMatchScore(searchIndex.province, searchQuery) * 2,
    fieldMatchScore(searchIndex.country, searchQuery) * 2,
    fieldMatchScore(searchIndex.url, searchQuery) * 1,
  );
};

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (char) => {
    switch (char) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return char;
    }
  });

const domainFromUrl = (rawUrl) => {
  try {
    const value = String(rawUrl ?? "");
    const url = value.startsWith("http") ? value : "https://" + value;
    return new URL(url).hostname.replace(/[^a-z0-9.-]/gi, "_");
  } catch {
    return null;
  }
};

const capitalizeFirstLetter = (value) => {
  const text = String(value ?? "");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
};

const buildDisplayUrl = (rawUrl) => {
  try {
    const value = String(rawUrl ?? "").trim();
    if (!value) return null;
    const normalized = value.startsWith("http") ? value : `https://${value}`;
    const parsed = new URL(normalized);
    return {
      href: parsed.href,
      label: parsed.host + parsed.pathname.replace(/\/+$/, ""),
    };
  } catch {
    return null;
  }
};

const buildInfoWindowHeaderContent = (destination) => {
  const container = document.createElement("div");
  container.style.display = "flex";
  container.style.alignItems = "center";
  container.style.justifyContent = "flex-start";
  container.style.flexWrap = "wrap";
  container.style.height = "48px";
  container.style.minHeight = "48px";
  container.style.lineHeight = "1";
  container.style.boxSizing = "border-box";

  [...(destination.type ?? [])].sort().forEach((type) => {
    const chip = document.createElement("span");
    chip.textContent = type;
    chip.style.backgroundColor = getTypeColor(type);
    chip.style.color = "#000000";
    chip.style.display = "inline-block";
    chip.style.padding = "3px 10px";
    chip.style.borderRadius = "12px";
    chip.style.margin = "0 6px 0 0";
    chip.style.textTransform = "uppercase";
    chip.style.fontWeight = "700";
    chip.style.fontSize = "10px";
    chip.style.letterSpacing = "0.5px";
    container.appendChild(chip);
  });

  return container;
};

const buildInfoWindowContent = (destination) => {
  const destinationTypes = Array.isArray(destination.type) ? destination.type : [destination.type];
  const showIac = destinationTypes.some((type) => String(type).trim().toLowerCase() === "iac");
  const screenshotDomain = domainFromUrl(destination.url);
  const screenshotSrc = screenshotDomain
    ? `/images/destination_screenshots/${screenshotDomain}.jpg`
    : null;
  const displayUrl = buildDisplayUrl(destination.url);
  const locationText = [
    destination.city,
    destination.province,
    destination.country,
  ]
    .filter(Boolean)
    .map(capitalizeFirstLetter)
    .map(escapeHtml)
    .join(", ");
  const websiteLink = displayUrl
    ? `<a href="${escapeHtml(displayUrl.href)}" target="_blank" rel="noopener noreferrer" aria-label="Go to URL for ${escapeHtml(destination.name)}" title="Go to URL" style="display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;color:#757575;text-decoration:none">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
    </a>`
    : "";

  return `
    <div style="width:260px;font-family:'Work Sans',Roboto,Arial,sans-serif;border-radius:12px;overflow:hidden">
      ${
        screenshotSrc
          ? `<div style="width:100%;height:140px;overflow:hidden;background:#e0e0e0;display:flex;align-items:center;justify-content:center">
        <img src="${screenshotSrc}" alt="" style="width:100%;height:100%;object-fit:cover" onerror="this.parentElement.style.display='none'">
      </div>`
          : ""
      }
      <div style="padding:10px 14px 8px">
        <div style="font-size:15px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;line-height:1.2;margin-bottom:4px">${escapeHtml(destination.name)}</div>
        <div style="font-size:11px;color:#666;margin-bottom:4px">${locationText}</div>
        ${(displayUrl || showIac) ? `<div style="display:flex;align-items:center;justify-content:space-between;min-height:34px;margin-top:8px;padding-top:6px;border-top:1px solid #ececec">
          ${websiteLink}
          ${showIac ? `<img src="/images/iac-logo.png" alt="IAC" title="Member of the International Academy of Ceramics" style="display:block;width:34px;height:32px;object-fit:contain;object-position:right center;margin-left:auto">` : ""}
        </div>` : ""}
      </div>
    </div>`;
};

const WorldMap = ({ devOptions = DEFAULT_DEV_OPTIONS, onZoomChange }) => {
  const {
    searchTerm: searchString,
    screenSize,
    locationsLoaded,
    dispatch,
    countryFilter,
    typeFilter,
    types,
    destinations,
  } = useContext(store);
  const [markers, setMarkers] = useState([]);
  const searchTerm = searchString?.toLowerCase() ?? "";
  const preparedSearch = useMemo(
    () => prepareSearchText(searchTerm),
    [searchTerm],
  );
  const [map, setMap] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const destinationId = searchParams.get("destinationId");
  const hasZoomedToDestination = useRef(false);
  const [boundsFilterEnabled, setBoundsFilterEnabled] = useState(false);
  const [boundsTick, setBoundsTick] = useState(0);
  const [showList, setShowList] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [mapStyleKey, setMapStyleKey] = useState("terracotta");
  const safeDevOptions = useMemo(
    () => getSafeDevOptions(devOptions),
    [devOptions],
  );
  const mapOptions = useMemo(
    () =>
      buildMapOptions(
        mapStyleKey === "default" ? defaultStyle : terracottaStyle,
        safeDevOptions,
      ),
    [mapStyleKey, safeDevOptions],
  );

  const markerCluster = useRef(null);
  const sharedInfoWindow = useRef(null);
  const openInfoWindowMarker = useRef(null);
  const pinnedInfoWindowMarker = useRef(null);
  const markersRef = useRef([]);
  const clusterReadyRef = useRef(false);
  const devOptionsRef = useRef(safeDevOptions);
  const markerIconFrameRef = useRef(null);
  const mapStyleRef = useRef("terracotta");
  const infoWindowCloseTimer = useRef(null);

  const updateMarkerIcons = useCallback((zoom) => {
    markersRef.current.forEach(({ location, googleMarker }) => {
      googleMarker.setIcon(
        getMarkerIcon(
          location.type,
          location.sponsored,
          zoom,
          devOptionsRef.current.markerSize,
        ),
      );
    });
  }, []);

  useEffect(() => {
    devOptionsRef.current = safeDevOptions;
  }, [safeDevOptions]);

  useEffect(() => {
    map?.setOptions(mapOptions);
  }, [map, mapOptions]);

  useEffect(() => {
    if (!map || !markersRef.current.length) return;
    updateMarkerIcons(map.getZoom());
  }, [map, safeDevOptions.markerSize, updateMarkerIcons]);

  const closeActiveInfoWindow = useCallback(() => {
    if (infoWindowCloseTimer.current) {
      window.clearTimeout(infoWindowCloseTimer.current);
      infoWindowCloseTimer.current = null;
    }
    sharedInfoWindow.current?.close();
    openInfoWindowMarker.current = null;
    pinnedInfoWindowMarker.current = null;
  }, []);

  const scheduleInfoWindowClose = useCallback(() => {
    if (
      pinnedInfoWindowMarker.current &&
      pinnedInfoWindowMarker.current === openInfoWindowMarker.current
    ) {
      return;
    }

    if (infoWindowCloseTimer.current) {
      window.clearTimeout(infoWindowCloseTimer.current);
    }

    infoWindowCloseTimer.current = window.setTimeout(() => {
      if (
        pinnedInfoWindowMarker.current &&
        pinnedInfoWindowMarker.current === openInfoWindowMarker.current
      ) {
        infoWindowCloseTimer.current = null;
        return;
      }

      sharedInfoWindow.current?.close();
      openInfoWindowMarker.current = null;
      infoWindowCloseTimer.current = null;
    }, 300);
  }, []);

  const openMarkerInfoWindow = useCallback(
    (googleMarker, markerContent, shouldPin = false, source = "marker") => {
      if (
        source === "card" &&
        pinnedInfoWindowMarker.current &&
        pinnedInfoWindowMarker.current !== googleMarker
      ) {
        return;
      }

      if (infoWindowCloseTimer.current) {
        window.clearTimeout(infoWindowCloseTimer.current);
        infoWindowCloseTimer.current = null;
      }

      if (shouldPin === true) {
        pinnedInfoWindowMarker.current = googleMarker;
      } else if (pinnedInfoWindowMarker.current !== googleMarker) {
        pinnedInfoWindowMarker.current = null;
      }

      const content =
        typeof markerContent === "string" ? markerContent : markerContent.body;
      const headerContent =
        typeof markerContent === "string" ? "" : markerContent.header;

      sharedInfoWindow.current.setContent(content);
      if (typeof sharedInfoWindow.current.setHeaderContent === "function") {
        sharedInfoWindow.current.setHeaderContent(headerContent);
      } else {
        sharedInfoWindow.current.setOptions({ headerContent });
      }
      try {
        sharedInfoWindow.current.open(map, googleMarker);
      } catch {
        sharedInfoWindow.current.open({
          anchor: googleMarker,
          map,
          shouldFocus: false,
        });
      }
      openInfoWindowMarker.current = googleMarker;
    },
    [map],
  );

  const createMarkerClusterer = useCallback(
    (initialMarkers = []) =>
      new MarkerClusterer({
        map,
        markers: initialMarkers,
        algorithm: new SuperClusterAlgorithm({
          radius: 80,
          maxZoom: 15,
          minPoints: 8,
        }),
        renderer: createClusterRenderer(devOptionsRef),
        onClusterClick: (event, cluster, clusterMap) => {
          closeActiveInfoWindow();
          if (cluster.bounds) clusterMap.fitBounds(cluster.bounds);
        },
      }),
    [closeActiveInfoWindow, map],
  );

  useEffect(() => {
    if (!map || !markersRef.current.length) return;

    const visibleMarkers = markersRef.current
      .filter((marker) => marker.display)
      .map((marker) => marker.googleMarker);

    markerCluster.current?.clearMarkers(true);
    markerCluster.current?.setMap(null);
    markerCluster.current = createMarkerClusterer(visibleMarkers);
    clusterReadyRef.current = true;
  }, [
    createMarkerClusterer,
    map,
    safeDevOptions.clusterColor,
    safeDevOptions.clusterSize,
    safeDevOptions.clusterTransparency,
  ]);

  const classes = useStyles({
    showMap,
    showList,
    loaded: locationsLoaded,
    mobile: screenSize,
  });

  const toggleShowMap = () => {
    setShowMap(!showMap);
    setShowList(!showList);
  };

  const handleMobileCardClick = useCallback(() => {
    if (isMobile(screenSize)) {
      setShowMap(true);
      setShowList(false);
    }
  }, [screenSize]);

  const showAllResults = useCallback(() => {
    setBoundsFilterEnabled(false);
    dispatch({ type: "SET_SEARCH_TERM", payload: "" });
    dispatch({ type: "SET_TYPE_FILTER", payload: [] });
    dispatch({ type: "SET_COUNTRY_FILTER", payload: [] });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("destinationId");
    nextParams.delete("country");
    nextParams.delete("type");
    setSearchParams(nextParams);

    if (map && markers.length) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => {
        bounds.extend(marker.googleMarker.getPosition());
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
      }
    }
  }, [dispatch, map, markers, searchParams, setSearchParams]);

  const getMarkerFilterState = useCallback(
    (marker) => {
      const location = marker.location;
      if (destinationId) {
        return {
          matches: location.id === destinationId,
          score: 0,
        };
      }

      const searchScore = preparedSearch.text
        ? markerMatchScore(marker.searchIndex, preparedSearch)
        : 0;
      const includesTerm = preparedSearch.text ? searchScore > 0 : true;

      const includesType =
        typeFilter.length > 0
          ? location.type?.some((type) => typeFilter.indexOf(type) >= 0)
          : true;

      const includesCountry =
        countryFilter.length > 0
          ? countryFilter.includes(location.country)
          : true;

      const matches = includesTerm && includesType && includesCountry;
      return {
        matches,
        score: matches ? searchScore : 0,
      };
    },
    [countryFilter, destinationId, preparedSearch, typeFilter],
  );

  useLayoutEffect(() => {
    setShowMap(true);
    setShowList(!isMobile(screenSize));
  }, [screenSize]);

  // switch the map style as the user zooms in to assist readability
  useEffect(() => {
    if (!map) return;
    const initialZoom = map.getZoom() ?? 0;
    onZoomChange?.(initialZoom);
    mapStyleRef.current = initialZoom >= 15 ? "default" : "terracotta";

    const zoomListener = map.addListener("zoom_changed", () => {
      closeActiveInfoWindow();
      const currentZoom = map.getZoom() ?? 0;
      onZoomChange?.(currentZoom);
      if (markerIconFrameRef.current) {
        window.cancelAnimationFrame(markerIconFrameRef.current);
      }
      markerIconFrameRef.current = window.requestAnimationFrame(() => {
        updateMarkerIcons(map.getZoom());
        markerIconFrameRef.current = null;
      });

      const nextStyleKey = currentZoom >= 15 ? "default" : "terracotta";
      if (mapStyleRef.current !== nextStyleKey) {
        mapStyleRef.current = nextStyleKey;
        setMapStyleKey(nextStyleKey);
      }
    });

    return () => {
      if (markerIconFrameRef.current) {
        window.cancelAnimationFrame(markerIconFrameRef.current);
        markerIconFrameRef.current = null;
      }
      window.google.maps.event.removeListener(zoomListener);
    };
  }, [closeActiveInfoWindow, map, onZoomChange, updateMarkerIcons]);

  // Recompute visible sidebar results whenever the viewport changes.
  useEffect(() => {
    if (!map || !boundsFilterEnabled) return;
    const idleListener = map.addListener("idle", () => {
      setBoundsTick((tick) => tick + 1);
    });

    return () => {
      window.google.maps.event.removeListener(idleListener);
    };
  }, [boundsFilterEnabled, map]);

  useEffect(() => {
    if (!map || !window.google || markerCluster.current) return;

    markerCluster.current = createMarkerClusterer([]);

    return () => {
      closeActiveInfoWindow();
      markerCluster.current?.clearMarkers(true);
      markerCluster.current?.setMap(null);
      markerCluster.current = null;
    };
  }, [closeActiveInfoWindow, createMarkerClusterer, map]);

  /*
  Iterate through all of our AirTable data and render each marker.
  This is called only once the map is rendered and redux state has been populated with our locations.
  */
  useEffect(() => {
    if (!map || !window.google || !types.length) return;

    // Create or reuse a single shared InfoWindow — guarantees only one open at a time.
    if (!sharedInfoWindow.current) {
      sharedInfoWindow.current = new window.google.maps.InfoWindow({
        disableAutoPan: true,
      });
      sharedInfoWindow.current.addListener("domready", () => {
        const iwContainer = document.querySelector(".gm-style-iw");
        if (iwContainer) {
          iwContainer.addEventListener("wheel", (e) => e.stopPropagation(), {
            passive: false,
          });
        }
        const iwHeaderContent = document.querySelector(".gm-style-iw-ch");
        if (iwHeaderContent) {
          iwHeaderContent.style.paddingTop = "0";
        }
      });
      sharedInfoWindow.current.addListener("closeclick", () => {
        openInfoWindowMarker.current = null;
        pinnedInfoWindowMarker.current = null;
      });
    }

    const marks = destinations;
    const allMarkers = [];
    const errors = [];
    const currentZoom = map.getZoom();
    clusterReadyRef.current = false;

    marks?.forEach((mark) => {
      if (
        mark.lat === null ||
        mark.lng === null ||
        isNaN(mark.lat) ||
        isNaN(mark.lng) ||
        mark.lat === undefined ||
        mark.lng === undefined
      ) {
        errors.push(mark);
        return null;
      }

      // create our markers
      const newMarker = new window.google.maps.Marker({
        draggable: false,
        position: { lat: mark.lat, lng: mark.lng },
        title: `${mark.url}`,
        icon: getMarkerIcon(
          mark.type,
          mark.sponsored,
          currentZoom,
          devOptionsRef.current.markerSize,
        ),
        optimized: true,
      });
      let markerContent = null;
      newMarker.addListener("mouseover", function (eventOrShouldPin = false) {
        markerContent ||= {
          body: buildInfoWindowContent(mark),
          header: buildInfoWindowHeaderContent(mark),
        };
        openMarkerInfoWindow(
          newMarker,
          markerContent,
          eventOrShouldPin === true,
          eventOrShouldPin === "card-hover" ? "card" : "marker",
        );
      });
      newMarker.addListener("mouseout", function () {
        scheduleInfoWindowClose();
      });
      newMarker.addListener("click", function () {
        handleLinkClick(mark);
      });
      allMarkers.push({
        location: mark,
        searchIndex: createMarkerSearchIndex(mark),
        googleMarker: newMarker,
        display: true,
        score: 0,
      });
      // end marker creation
    });
    errors.length && console.log(errors);
    markersRef.current = allMarkers;
    setMarkers(allMarkers);

    return () => {
      closeActiveInfoWindow();
      markerCluster.current?.clearMarkers(true);
      markerCluster.current?.render();
      allMarkers.forEach(({ googleMarker }) => {
        window.google.maps.event.clearInstanceListeners(googleMarker);
        googleMarker.setMap(null);
      });
      markersRef.current = [];
      clusterReadyRef.current = false;
    };
  }, [
    closeActiveInfoWindow,
    destinations,
    map,
    openMarkerInfoWindow,
    scheduleInfoWindowClose,
    types,
  ]);

  // handle our filters - this is called every time a filter/searchterm changes
  useEffect(() => {
    hasZoomedToDestination.current = false;
  }, [destinationId]);

  useEffect(() => {
    if (!map || !window.google || !markerCluster.current) return;

    const currentBounds = map?.getBounds?.();
    const visibleMarkers = [];
    let markerStateChanged = false;
    let visibilityChanged = !clusterReadyRef.current;

    markers.forEach((marker) => {
      // When a search term is active, ignore viewport bounds so all matching
      // results appear in the sidebar regardless of where the map is panned.
      const hasActiveSearch =
        !!destinationId ||
        searchTerm.length > 0 ||
        typeFilter.length > 0 ||
        countryFilter.length > 0;
      const inBounds =
        hasActiveSearch ||
        !boundsFilterEnabled ||
        !currentBounds ||
        currentBounds.contains(marker.googleMarker.getPosition());

      const filterState = getMarkerFilterState(marker);
      const nextDisplay = filterState.matches && inBounds;
      const nextScore = nextDisplay ? filterState.score : 0;

      if (marker.display !== nextDisplay) {
        markerStateChanged = true;
        visibilityChanged = true;
        marker.googleMarker.setVisible(nextDisplay);

        if (!nextDisplay) {
          marker.googleMarker.setMap(null);
          if (openInfoWindowMarker.current === marker.googleMarker) {
            closeActiveInfoWindow();
          }
        }
      }

      if (marker.score !== nextScore) {
        markerStateChanged = true;
      }

      marker.display = nextDisplay;
      marker.score = nextScore;

      if (nextDisplay) {
        visibleMarkers.push(marker.googleMarker);
      }
    });

    if (visibilityChanged) {
      markerCluster.current.clearMarkers(true);
      markerCluster.current.addMarkers(visibleMarkers, true);
      markerCluster.current.render();
      clusterReadyRef.current = true;
    }

    if (markerStateChanged) {
      setMarkers([...markers]);
    }
  }, [
    boundsFilterEnabled,
    boundsTick,
    closeActiveInfoWindow,
    countryFilter,
    destinationId,
    getMarkerFilterState,
    map,
    markers,
    searchTerm,
    typeFilter,
  ]);

  // Fit viewport to matching results when search/filter terms change.
  // Intentionally excludes boundsTick so user panning/zooming is not overridden.
  useEffect(() => {
    if (!map || !markers.length || !window.google) return;
    if (
      searchTerm.length === 0 &&
      typeFilter.length === 0 &&
      countryFilter.length === 0 &&
      !destinationId
    )
      return;

    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((marker) => {
      if (getMarkerFilterState(marker).matches) {
        bounds.extend(marker.googleMarker.getPosition());
      }
    });
    if (!bounds.isEmpty()) map.fitBounds(bounds);
  }, [
    countryFilter,
    destinationId,
    getMarkerFilterState,
    map,
    markers,
    searchTerm,
    typeFilter,
  ]);

  // Zoom to a specific destination if ?destinationId= is present in the URL
  useEffect(() => {
    if (!map || !markers.length || hasZoomedToDestination.current) return;
    if (!destinationId) return;
    const match = markers.find((m) => m.location.id === destinationId);
    if (!match) return;
    hasZoomedToDestination.current = true;
    map.setCenter(match.googleMarker.getPosition());
    map.setZoom(14);
  }, [destinationId, map, markers]);

  return (
    <>
      <div
        className={
          isMobile(screenSize) ? classes.mobileContainer : classes.container
        }
      >
        {isMobile(screenSize) && (
          <Button
            variant="contained"
            onClick={toggleShowMap}
            className={classes.showMapButton}
          >
            {`${showMap ? "Search" : "Show Map"}`}
          </Button>
        )}
        <div className={classes.locationList}>
          {!locationsLoaded || !markers.length ? (
            <Box sx={{ margin: "auto", minHeight: "200px" }}>
              <CircularProgress
                title="Loading"
                thickness={2.5}
                variant="indeterminate"
                size={100}
              />
            </Box>
          ) : (
            <>
              <Searchbar />
              <DestinationsPanel
                markers={markers}
                map={map}
                showAllButton={boundsFilterEnabled || !!destinationId}
                onShowAll={showAllResults}
                onCardClick={handleMobileCardClick}
              />
            </>
          )}
        </div>
        <div className={classes.mapContainer}>
          <GoogleMapReact
            onGoogleApiLoaded={({ map }) => {
              setMap(map);
              onZoomChange?.(map.getZoom() ?? 0);
            }}
            bootstrapURLKeys={{
              key: "AIzaSyCE2rTvxjbMDkkBSYqc3ZIjOIZSi6qR2Lc",
            }}
            defaultCenter={defaultCenter}
            defaultZoom={0}
            gestureHandling="greedy"
            options={mapOptions}
            yesIWantToUseGoogleMapApiInternals={true}
          />
        </div>
      </div>
    </>
  );
};

export default WorldMap;
